/**
 * Food & Beverage Industry Configuration
 *
 * Supports: Filling Lines, Packaging Machines, Mixers, Pasteurizers, Refrigeration Units, Conveyors
 */

import { IndustryConfig } from '../industryTypes'

export const foodBeverageConfig: IndustryConfig = {
  id: 'food_beverage',
  name: 'Food & Beverage',
  description: 'Food and beverage processing and packaging equipment monitoring',
  icon: 'Cookie',

  terminology: {
    asset: 'Production Line',
    assets: 'Production Lines',
    assetPlural: 'Production Lines',
    assetType: 'Line Type',
    failure: 'Line Stop',
    health: 'Status',
    healthScore: 'Line Health Score',
    healthy: 'Running',
    degraded: 'Degraded',
    critical: 'Stopped',
    alert: 'Food Safety Alert',
    process: 'Production',
    maintenance: 'Sanitation & Maintenance',
    inspection: 'Audit',
    downtime: 'Line Downtime',
    workOrder: 'Service Request',
    prediction: 'Forecast',
    anomaly: 'Deviation',
    trend: 'Trend',
    risk: 'Food Safety Risk',
    facility: 'Plant',
    facilityPlural: 'Plants',
    location: 'Line',
    operator: 'Line Operator',
    supervisor: 'Production Supervisor',
    calibration: 'Instrument Calibration',
    compliance: 'Food Safety Compliance',
    criticality: 'Line Criticality',
  },

  assetTypes: [
    {
      id: 'filling_line',
      name: 'Filling Line',
      icon: 'Droplets',
      description: 'Liquid and viscous product filling systems',
      category: 'Filling',
      defaultMetrics: ['fill_volume', 'fill_accuracy', 'cycle_time', 'temperature'],
      maintenanceTypes: ['sanitation', 'valve_service', 'seal_replacement', 'calibration'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary production line', downtimeCostMultiplier: 15 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major throughput impact', downtimeCostMultiplier: 8 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary line', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup capacity', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'packaging_machine',
      name: 'Packaging Machine',
      icon: 'Package',
      description: 'Primary and secondary packaging equipment',
      category: 'Packaging',
      defaultMetrics: ['packages_per_minute', 'seal_integrity', 'label_accuracy', 'film_tension'],
      maintenanceTypes: ['blade_replacement', 'seal_bar_service', 'sensor_calibration', 'lubrication'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Line bottleneck', downtimeCostMultiplier: 12 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary packager', downtimeCostMultiplier: 6 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary packager', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Redundant unit', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'mixer',
      name: 'Mixer',
      icon: 'RefreshCw',
      description: 'Batch and continuous mixing equipment',
      category: 'Processing',
      defaultMetrics: ['mixing_speed', 'torque', 'temperature', 'viscosity'],
      maintenanceTypes: ['blade_inspection', 'seal_replacement', 'bearing_service', 'sanitation'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary batch mixer', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Continuous mixer', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary mixer', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup unit', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'pasteurizer',
      name: 'Pasteurizer',
      icon: 'Thermometer',
      description: 'Heat treatment and pasteurization systems',
      category: 'Processing',
      defaultMetrics: ['temperature', 'holding_time', 'flow_rate', 'pressure'],
      maintenanceTypes: ['plate_inspection', 'gasket_replacement', 'CIP', 'thermocouple_calibration'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Single pasteurizer', downtimeCostMultiplier: 20 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary unit', downtimeCostMultiplier: 10 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary unit', downtimeCostMultiplier: 4 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Standby unit', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'refrigeration_unit',
      name: 'Refrigeration Unit',
      icon: 'Snowflake',
      description: 'Cold storage and process cooling systems',
      category: 'Utilities',
      defaultMetrics: ['temperature', 'compressor_pressure', 'refrigerant_level', 'energy_consumption'],
      maintenanceTypes: ['compressor_service', 'refrigerant_check', 'coil_cleaning', 'defrost_cycle'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Cold chain critical', downtimeCostMultiplier: 25 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Process cooling', downtimeCostMultiplier: 12 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Storage cooling', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup refrigeration', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'conveyor',
      name: 'Conveyor System',
      icon: 'ArrowRightLeft',
      description: 'Food-grade material handling conveyors',
      category: 'Material Handling',
      defaultMetrics: ['belt_speed', 'motor_current', 'belt_tension', 'product_count'],
      maintenanceTypes: ['belt_sanitation', 'belt_inspection', 'motor_service', 'alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main production line', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Infeed conveyor', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Transfer conveyor', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Accumulation conveyor', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'food_safety',
      name: 'Food Safety',
      icon: 'ShieldCheck',
      description: 'Food safety and hygiene monitoring',
      businessValue: 'Ensure product safety and regulatory compliance',
      category: 'industry_specific',
      metrics: [
        { id: 'ccp_compliance', name: 'CCP Compliance', unit: '%', description: 'Critical Control Point compliance rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 99, critical: 98 } },
        { id: 'sanitation_score', name: 'Sanitation Score', unit: '%', description: 'Sanitation audit score', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'pathogen_tests', name: 'Pathogen Test Results', unit: 'pass/fail', description: 'Microbiological test pass rate', dataType: 'discrete', aggregation: 'count', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 99.5, critical: 99 } },
        { id: 'foreign_material', name: 'Foreign Material Detection', unit: 'incidents', description: 'Foreign material incidents per million units', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 5 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['gauge', 'line_chart', 'bar_chart', 'trend'],
    },
    {
      id: 'production_efficiency',
      name: 'Production Efficiency',
      icon: 'Gauge',
      description: 'Overall production line effectiveness',
      businessValue: 'Maximize throughput and minimize waste',
      category: 'universal',
      metrics: [
        { id: 'line_efficiency', name: 'Line Efficiency', unit: '%', description: 'Actual vs theoretical output', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 75, critical: 65 } },
        { id: 'yield', name: 'Product Yield', unit: '%', description: 'Finished product vs raw material input', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 92 } },
        { id: 'changeover_time', name: 'Changeover Time', unit: 'min', description: 'Time to switch products', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 30, warning: 45, critical: 60 } },
        { id: 'throughput', name: 'Throughput', unit: 'units/hr', description: 'Units produced per hour', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 1000, warning: 800, critical: 600 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['line_chart', 'bar_chart', 'comparison', 'trend'],
    },
    {
      id: 'quality_control',
      name: 'Quality Control',
      icon: 'CheckCircle',
      description: 'Product quality and consistency monitoring',
      businessValue: 'Reduce waste, rework, and customer complaints',
      category: 'industry_specific',
      metrics: [
        { id: 'first_pass_quality', name: 'First Pass Quality', unit: '%', description: 'Products passing first inspection', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 97, critical: 95 } },
        { id: 'fill_accuracy', name: 'Fill Accuracy', unit: '%', description: 'Fill weight/volume accuracy', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.5, warning: 99, critical: 98 } },
        { id: 'seal_integrity', name: 'Seal Integrity', unit: '%', description: 'Package seal pass rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.9, warning: 99.5, critical: 99 } },
        { id: 'customer_complaints', name: 'Customer Complaints', unit: 'ppm', description: 'Complaints per million units sold', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 25, critical: 50 } },
      ],
      thresholds: { healthy: { min: 99 }, warning: { min: 97, max: 99 }, critical: { max: 97 } },
      visualizations: ['bar_chart', 'trend', 'distribution', 'comparison'],
    },
    {
      id: 'energy_efficiency',
      name: 'Energy Efficiency',
      icon: 'Zap',
      description: 'Energy consumption and sustainability metrics',
      businessValue: 'Reduce operating costs and environmental impact',
      category: 'universal',
      metrics: [
        { id: 'energy_per_unit', name: 'Energy per Unit', unit: 'kWh/unit', description: 'Energy consumption per product unit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 0.7, critical: 1.0 } },
        { id: 'water_usage', name: 'Water Usage', unit: 'gal/unit', description: 'Water consumption per product unit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 3, critical: 5 } },
        { id: 'refrigeration_efficiency', name: 'Refrigeration COP', unit: 'ratio', description: 'Coefficient of performance', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 4.0, warning: 3.5, critical: 3.0 } },
        { id: 'steam_efficiency', name: 'Steam Efficiency', unit: '%', description: 'Steam system efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 75, critical: 65 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['line_chart', 'bar_chart', 'trend', 'comparison'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'sanitation',
      name: 'Sanitation',
      description: 'Cleaning and sanitization procedures',
      category: 'preventive',
      defaultInterval: { value: 8, unit: 'hours' },
      laborRequirements: [
        { role: 'Sanitation Technician', count: 2, hourlyRate: 35 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['sanitation', 'food_safety'],
      partsCommonly: ['sanitizers', 'gaskets', 'brushes'],
    },
    {
      id: 'preventive',
      name: 'Preventive Maintenance',
      description: 'Scheduled time-based maintenance',
      category: 'preventive',
      defaultInterval: { value: 500, unit: 'hours' },
      laborRequirements: [
        { role: 'Maintenance Technician', count: 1, hourlyRate: 48 },
      ],
      estimatedDuration: { min: 1, typical: 2, max: 4, unit: 'hours' },
      skillsRequired: ['mechanical', 'electrical', 'food_grade'],
      partsCommonly: ['seals', 'gaskets', 'lubricants', 'filters'],
    },
    {
      id: 'corrective',
      name: 'Corrective Maintenance',
      description: 'Repair after failure or deviation',
      category: 'corrective',
      laborRequirements: [
        { role: 'Maintenance Technician', count: 2, hourlyRate: 48 },
        { role: 'Quality Technician', count: 1, hourlyRate: 42 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 12, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'mechanical', 'electrical', 'sanitation'],
      partsCommonly: ['motors', 'valves', 'sensors', 'seals'],
    },
    {
      id: 'cip',
      name: 'CIP (Clean-in-Place)',
      description: 'Automated cleaning of process equipment',
      category: 'condition_based',
      defaultInterval: { value: 24, unit: 'hours' },
      laborRequirements: [
        { role: 'Process Operator', count: 1, hourlyRate: 38 },
      ],
      estimatedDuration: { min: 45, typical: 90, max: 180, unit: 'minutes' },
      skillsRequired: ['cip_operation', 'food_safety', 'chemical_handling'],
      partsCommonly: ['caustic_solution', 'acid_solution', 'sanitizer', 'gaskets'],
    },
  ],

  riskFactors: [
    { id: 'food_safety_risk', name: 'Food Safety Risk', description: 'Risk of product contamination or safety issue', category: 'safety', weight: 0.35, calculationMethod: 'exponential', inputs: ['ccp_status', 'sanitation_score', 'temperature_deviation'] },
    { id: 'contamination_risk', name: 'Contamination Risk', description: 'Risk of foreign material or cross-contamination', category: 'safety', weight: 0.25, calculationMethod: 'threshold', inputs: ['allergen_status', 'foreign_material_detection', 'seal_integrity'] },
    { id: 'operational_risk', name: 'Operational Risk', description: 'Risk of unplanned line stoppage', category: 'operational', weight: 0.25, calculationMethod: 'linear', inputs: ['equipment_health', 'maintenance_due', 'age'] },
    { id: 'regulatory_risk', name: 'Regulatory Risk', description: 'Risk of compliance violation', category: 'compliance', weight: 0.15, calculationMethod: 'threshold', inputs: ['audit_score', 'documentation_status', 'training_compliance'] },
  ],

  complianceStandards: [
    {
      id: 'fda_fsma',
      name: 'FDA Food Safety Modernization Act',
      shortName: 'FSMA',
      description: 'Federal food safety regulations for preventive controls',
      authority: 'FDA',
      region: ['US'],
      requirements: [
        { id: 'hazard_analysis', name: 'Hazard Analysis', description: 'Written hazard analysis required', kpiId: 'hazard_analysis_complete', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'preventive_controls', name: 'Preventive Controls', description: 'Implemented preventive controls', kpiId: 'preventive_controls_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'monitoring', name: 'Monitoring Procedures', description: 'CCP monitoring documented', kpiId: 'monitoring_compliance', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'corrective_actions', name: 'Corrective Actions', description: 'Corrective action procedures in place', kpiId: 'corrective_action_status', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'daily',
    },
    {
      id: 'haccp',
      name: 'Hazard Analysis Critical Control Points',
      shortName: 'HACCP',
      description: 'Systematic approach to food safety hazard control',
      authority: 'Codex Alimentarius',
      region: ['Global'],
      requirements: [
        { id: 'ccp_monitoring', name: 'CCP Monitoring', description: 'All CCPs monitored continuously', kpiId: 'ccp_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'critical_limits', name: 'Critical Limits', description: 'Critical limits established and maintained', kpiId: 'critical_limits_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'verification', name: 'Verification Procedures', description: 'HACCP plan verified regularly', kpiId: 'verification_status', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'record_keeping', name: 'Record Keeping', description: 'Complete HACCP documentation', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'daily',
    },
    {
      id: 'sqf',
      name: 'Safe Quality Food',
      shortName: 'SQF',
      description: 'GFSI-recognized food safety certification program',
      authority: 'SQFI',
      region: ['Global'],
      requirements: [
        { id: 'food_safety_plan', name: 'Food Safety Plan', description: 'Documented food safety plan', kpiId: 'food_safety_plan_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'quality_plan', name: 'Quality Plan', description: 'Documented quality plan', kpiId: 'quality_plan_status', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'internal_audits', name: 'Internal Audits', description: 'Regular internal audits conducted', kpiId: 'internal_audit_score', threshold: 85, operator: 'gte', severity: 'required' },
        { id: 'supplier_approval', name: 'Supplier Approval', description: 'All suppliers approved', kpiId: 'supplier_approval_status', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'brc',
      name: 'British Retail Consortium Global Standard',
      shortName: 'BRC',
      description: 'GFSI-recognized certification for food safety',
      authority: 'BRCGS',
      region: ['Global'],
      requirements: [
        { id: 'senior_management', name: 'Senior Management Commitment', description: 'Demonstrated management commitment', kpiId: 'management_review_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'food_safety_culture', name: 'Food Safety Culture', description: 'Documented food safety culture program', kpiId: 'food_safety_culture_score', threshold: 80, operator: 'gte', severity: 'required' },
        { id: 'site_standards', name: 'Site Standards', description: 'Facility meets BRC site standards', kpiId: 'site_inspection_score', threshold: 90, operator: 'gte', severity: 'required' },
        { id: 'product_control', name: 'Product Control', description: 'Product control procedures in place', kpiId: 'product_control_status', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  visualization: {
    primaryColor: '#10b981',
    secondaryColor: '#34d399',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Status Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Forecast Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    {
      id: 'food_safety_alert',
      name: 'Food Safety Alert',
      context: 'asset_health',
      audience: ['manager', 'analyst', 'technician'],
      template: 'Food Safety Alert for {assetType} "{assetName}": {alertDetails}. CCP Status: {ccpStatus}. Immediate action required: {actionRequired}. Contact Food Safety Manager immediately if deviation persists.',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'alertDetails', type: 'metric' },
        { name: 'ccpStatus', type: 'metric' },
        { name: 'actionRequired', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 15,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Food & Beverage-Specific)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'food_safety_manager',
      category: 'sme',
      name: 'Food Safety Manager',
      shortName: 'FSM',
      description: 'Oversees food safety programs, HACCP compliance, and regulatory requirements',
      responsibilities: [
        'HACCP plan development and maintenance',
        'Food safety training program management',
        'Regulatory audit preparation',
        'Supplier food safety qualification',
        'Food safety incident investigation',
      ],
      focusAreas: ['ccp_compliance', 'pathogen_control', 'allergen_management', 'sanitation_verification', 'regulatory_compliance'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['compliance', 'risk', 'action_items'],
      },
      decisionAuthority: ['food_safety_hold', 'product_release', 'sanitation_approval', 'supplier_qualification'],
      reportingTo: 'Plant Manager',
      icon: 'ShieldCheck',
    },
    {
      id: 'quality_manager',
      category: 'sme',
      name: 'Quality Assurance Manager',
      shortName: 'QAM',
      description: 'Manages quality systems, product specifications, and continuous improvement',
      responsibilities: [
        'Quality system management',
        'Product specification development',
        'Customer complaint investigation',
        'Quality audit management',
        'Continuous improvement initiatives',
      ],
      focusAreas: ['product_quality', 'specification_compliance', 'customer_satisfaction', 'process_capability', 'defect_reduction'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact', 'action_items'],
      },
      decisionAuthority: ['quality_hold', 'specification_changes', 'supplier_approval', 'process_adjustments'],
      reportingTo: 'Plant Manager',
      icon: 'CheckCircle',
    },
    {
      id: 'production_manager',
      category: 'sme',
      name: 'Production Manager',
      shortName: 'PDM',
      description: 'Manages production operations, scheduling, and line efficiency',
      responsibilities: [
        'Production scheduling and planning',
        'Line efficiency optimization',
        'Resource allocation',
        'Production KPI management',
        'Changeover optimization',
      ],
      focusAreas: ['line_efficiency', 'throughput', 'schedule_adherence', 'waste_reduction', 'labor_utilization'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['business_impact', 'action_items', 'risk'],
      },
      decisionAuthority: ['production_scheduling', 'line_assignments', 'overtime_approval', 'changeover_timing'],
      reportingTo: 'Plant Manager',
      icon: 'Factory',
    },
    // Technician Roles
    {
      id: 'maintenance_technician',
      category: 'technician',
      name: 'Maintenance Technician',
      shortName: 'MT',
      description: 'Performs equipment maintenance, repairs, and troubleshooting in food-grade environment',
      responsibilities: [
        'Equipment troubleshooting and repair',
        'Preventive maintenance execution',
        'Food-grade equipment sanitation',
        'Spare parts management',
        'Work order completion',
      ],
      focusAreas: ['equipment_status', 'repair_procedures', 'parts_needed', 'safety_requirements', 'sanitation_requirements'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['repair_methods', 'parts_selection', 'immediate_safety_stops'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    {
      id: 'sanitation_technician',
      category: 'technician',
      name: 'Sanitation Technician',
      shortName: 'ST',
      description: 'Executes sanitation procedures, CIP operations, and hygiene verification',
      responsibilities: [
        'CIP system operation',
        'Manual cleaning procedures',
        'Sanitation verification testing',
        'Chemical handling and mixing',
        'Environmental monitoring',
      ],
      focusAreas: ['sanitation_procedures', 'chemical_concentrations', 'verification_results', 'environmental_swabs'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items', 'compliance'],
      },
      decisionAuthority: ['sanitation_methods', 'chemical_selection', 'verification_pass_fail'],
      reportingTo: 'Sanitation Supervisor',
      icon: 'Sparkles',
    },
    {
      id: 'quality_technician',
      category: 'technician',
      name: 'Quality Technician',
      shortName: 'QT',
      description: 'Performs quality testing, inspections, and documentation',
      responsibilities: [
        'In-process quality testing',
        'Finished product inspection',
        'Laboratory analysis',
        'Quality documentation',
        'Non-conformance reporting',
      ],
      focusAreas: ['test_results', 'specification_limits', 'sampling_procedures', 'hold_release_status'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'compliance'],
      },
      decisionAuthority: ['sampling_decisions', 'test_methods', 'non_conformance_identification'],
      reportingTo: 'Quality Supervisor',
      icon: 'ClipboardCheck',
    },
    // Executive Roles
    {
      id: 'plant_manager',
      category: 'executive',
      name: 'Plant Manager',
      shortName: 'PM',
      description: 'Oversees entire plant operations, safety, quality, and business performance',
      responsibilities: [
        'Overall plant performance',
        'Budget and cost management',
        'Food safety culture',
        'Regulatory compliance',
        'Staff development',
      ],
      focusAreas: ['oee', 'food_safety_score', 'cost_per_unit', 'safety_incidents', 'customer_satisfaction'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['capital_expenditure', 'staffing_levels', 'strategic_initiatives', 'regulatory_response'],
      reportingTo: 'VP Operations',
      icon: 'Building',
    },
    {
      id: 'operations_director',
      category: 'executive',
      name: 'Operations Director',
      shortName: 'OD',
      description: 'Directs multi-plant operations and strategic initiatives',
      responsibilities: [
        'Multi-plant performance oversight',
        'Strategic planning',
        'Capital project approval',
        'Best practice sharing',
        'Executive reporting',
      ],
      focusAreas: ['plant_rankings', 'strategic_kpis', 'capital_projects', 'risk_assessment', 'industry_benchmarks'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['capital_approval', 'plant_priorities', 'executive_escalations'],
      reportingTo: 'CEO',
      icon: 'Users',
    },
    // Analyst Roles
    {
      id: 'quality_analyst',
      category: 'analyst',
      name: 'Quality Analyst',
      shortName: 'QA',
      description: 'Analyzes quality data to identify trends, root causes, and improvement opportunities',
      responsibilities: [
        'Quality data analysis',
        'Trend identification',
        'Root cause investigation support',
        'Quality report generation',
        'SPC chart maintenance',
      ],
      focusAreas: ['quality_trends', 'defect_patterns', 'process_capability', 'specification_compliance', 'complaint_analysis'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['report_formats', 'analysis_methods', 'data_collection_priorities'],
      reportingTo: 'Quality Manager',
      icon: 'BarChart',
    },
    {
      id: 'production_analyst',
      category: 'analyst',
      name: 'Production Analyst',
      shortName: 'PA',
      description: 'Analyzes production data to optimize efficiency and reduce waste',
      responsibilities: [
        'Production data analysis',
        'Efficiency reporting',
        'Waste tracking and analysis',
        'Capacity planning support',
        'KPI dashboard maintenance',
      ],
      focusAreas: ['line_efficiency', 'downtime_analysis', 'yield_trends', 'changeover_optimization', 'scheduling_efficiency'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact', 'action_items'],
      },
      decisionAuthority: ['report_formats', 'analysis_priorities', 'data_collection_methods'],
      reportingTo: 'Production Manager',
      icon: 'TrendingUp',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Food & Beverage Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'sanitary_parts',
        name: 'Sanitary Parts',
        description: 'Food-grade sanitary fittings, valves, and components',
        parts: [
          {
            id: 'tri_clamp_gasket',
            partNumber: 'TC-GASKET-2IN',
            name: 'Tri-Clamp Gasket 2"',
            description: 'EPDM sanitary gasket, FDA compliant, 2 inch',
            category: 'sanitary_parts',
            manufacturer: 'Alfa Laval',
            unitCost: 8.50,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['filling_line', 'mixer', 'pasteurizer'],
            failureModes: ['gasket_leak', 'gasket_wear'],
            averageLifespan: { value: 6, unit: 'months' },
            specifications: { size: '2 inch', material: 'EPDM', rating: 'FDA/3-A' },
          },
          {
            id: 'sanitary_butterfly_valve',
            partNumber: 'SBV-3IN-PNEUM',
            name: 'Sanitary Butterfly Valve 3"',
            description: 'Pneumatic butterfly valve, 3 inch, 316L SS',
            category: 'sanitary_parts',
            manufacturer: 'Alfa Laval',
            unitCost: 850.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['filling_line', 'mixer', 'pasteurizer'],
            failureModes: ['valve_leak', 'actuator_failure', 'seal_wear'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { size: '3 inch', material: '316L SS', actuation: 'Pneumatic' },
          },
          {
            id: 'spray_ball',
            partNumber: 'SB-360-2IN',
            name: 'CIP Spray Ball 2"',
            description: '360-degree spray ball for CIP cleaning, 2 inch tri-clamp',
            category: 'sanitary_parts',
            manufacturer: 'SPX Flow',
            unitCost: 125.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['mixer', 'pasteurizer', 'filling_line'],
            failureModes: ['spray_pattern_degradation', 'clogging'],
            averageLifespan: { value: 2, unit: 'years' },
            specifications: { size: '2 inch', pattern: '360-degree', material: '316L SS' },
          },
          {
            id: 'sanitary_pump_impeller',
            partNumber: 'IMP-SAN-5HP',
            name: 'Sanitary Pump Impeller',
            description: 'Centrifugal pump impeller, 316L SS, for 5HP pump',
            category: 'sanitary_parts',
            manufacturer: 'Fristam',
            unitCost: 380.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['filling_line', 'mixer', 'pasteurizer'],
            failureModes: ['impeller_wear', 'cavitation_damage'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { size: '5HP', material: '316L SS', type: 'Open impeller' },
          },
        ],
      },
      {
        id: 'seals',
        name: 'Seals & Gaskets',
        description: 'Food-grade seals, O-rings, and gaskets',
        parts: [
          {
            id: 'pump_seal_kit',
            partNumber: 'PSK-SAN-5HP',
            name: 'Sanitary Pump Seal Kit',
            description: 'Complete mechanical seal kit for 5HP sanitary pump',
            category: 'seals',
            manufacturer: 'John Crane',
            unitCost: 245.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['filling_line', 'mixer', 'pasteurizer'],
            failureModes: ['seal_leak', 'seal_wear'],
            averageLifespan: { value: 12, unit: 'months' },
            specifications: { size: '5HP pump', material: 'Carbon/SiC/EPDM' },
          },
          {
            id: 'filler_valve_seal',
            partNumber: 'FVS-SILICON',
            name: 'Filler Valve Seal',
            description: 'Silicone seal for filling valve, FDA compliant',
            category: 'seals',
            manufacturer: 'Krones',
            unitCost: 18.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['filling_line'],
            failureModes: ['drip_leak', 'seal_wear', 'product_contamination'],
            averageLifespan: { value: 3, unit: 'months' },
            specifications: { material: 'Silicone', rating: 'FDA/3-A' },
          },
          {
            id: 'heat_exchanger_gasket',
            partNumber: 'HEX-GASKET-M6',
            name: 'Plate Heat Exchanger Gasket',
            description: 'NBR gasket for M6 plate heat exchanger',
            category: 'seals',
            manufacturer: 'Alfa Laval',
            unitCost: 65.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['pasteurizer'],
            failureModes: ['gasket_leak', 'thermal_degradation'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { model: 'M6', material: 'NBR', temperature: '-20 to 140C' },
          },
          {
            id: 'conveyor_belt_splice',
            partNumber: 'CBS-MODULAR',
            name: 'Conveyor Belt Splice Kit',
            description: 'Food-grade modular belt splice kit',
            category: 'seals',
            manufacturer: 'Intralox',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['conveyor'],
            failureModes: ['belt_separation', 'splice_failure'],
            averageLifespan: { value: 6, unit: 'months' },
            specifications: { type: 'Modular', material: 'Acetal', color: 'Blue detectable' },
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical Components',
        description: 'Motors, drives, and electrical components for food processing',
        parts: [
          {
            id: 'washdown_motor',
            partNumber: 'WDM-5HP-SS',
            name: 'Washdown Motor 5HP',
            description: 'Stainless steel washdown motor, 5HP, TEFC',
            category: 'electrical',
            manufacturer: 'Baldor',
            unitCost: 1850.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['conveyor', 'mixer', 'packaging_machine'],
            failureModes: ['motor_burnout', 'bearing_failure', 'seal_failure'],
            averageLifespan: { value: 8, unit: 'years' },
            specifications: { hp: 5, voltage: '460V', enclosure: 'TEFC Washdown', frame: '184TC SS' },
          },
          {
            id: 'vfd_ip66',
            partNumber: 'VFD-5HP-IP66',
            name: 'Washdown VFD 5HP',
            description: 'IP66 rated variable frequency drive for washdown environments',
            category: 'electrical',
            manufacturer: 'ABB',
            unitCost: 1450.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['conveyor', 'mixer', 'packaging_machine', 'filling_line'],
            failureModes: ['drive_fault', 'moisture_ingress', 'overheating'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { hp: 5, voltage: '460V', protection: 'IP66', mounting: 'Wall' },
          },
          {
            id: 'level_switch',
            partNumber: 'LS-SANITARY',
            name: 'Sanitary Level Switch',
            description: 'Tuning fork level switch, 3-A certified',
            category: 'electrical',
            manufacturer: 'Endress+Hauser',
            unitCost: 680.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['filling_line', 'mixer', 'pasteurizer'],
            failureModes: ['sensor_drift', 'coating_buildup'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { type: 'Tuning fork', connection: 'Tri-clamp', rating: '3-A' },
          },
          {
            id: 'control_panel_heater',
            partNumber: 'CPH-50W',
            name: 'Control Panel Heater',
            description: '50W panel heater for condensation prevention',
            category: 'electrical',
            manufacturer: 'Rittal',
            unitCost: 45.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['filling_line', 'packaging_machine', 'refrigeration_unit'],
            failureModes: ['heater_failure', 'thermostat_failure'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { wattage: '50W', voltage: '120V', mounting: 'DIN rail' },
          },
        ],
      },
      {
        id: 'sensors',
        name: 'Sensors & Instrumentation',
        description: 'Food-grade sensors for temperature, pressure, flow, and level',
        parts: [
          {
            id: 'sanitary_temp_sensor',
            partNumber: 'STS-PT100-TC',
            name: 'Sanitary Temperature Sensor',
            description: 'PT100 RTD with tri-clamp connection, 3-A certified',
            category: 'sensors',
            manufacturer: 'Endress+Hauser',
            unitCost: 285.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['pasteurizer', 'refrigeration_unit', 'mixer'],
            failureModes: ['sensor_drift', 'probe_damage', 'connection_failure'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { type: 'PT100', connection: 'Tri-clamp 1.5"', accuracy: '0.1C', rating: '3-A' },
          },
          {
            id: 'sanitary_pressure_sensor',
            partNumber: 'SPS-FLUSH-0-100',
            name: 'Sanitary Pressure Transmitter',
            description: 'Flush mount pressure transmitter, 0-100 psi, 3-A certified',
            category: 'sensors',
            manufacturer: 'Anderson-Negele',
            unitCost: 520.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['pasteurizer', 'filling_line', 'mixer'],
            failureModes: ['diaphragm_failure', 'sensor_drift', 'overpressure_damage'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { range: '0-100 psi', output: '4-20mA', connection: 'Tri-clamp', rating: '3-A' },
          },
          {
            id: 'flow_meter_mag',
            partNumber: 'FM-MAG-2IN',
            name: 'Magnetic Flow Meter 2"',
            description: 'Sanitary magnetic flow meter, 2 inch, EHEDG certified',
            category: 'sensors',
            manufacturer: 'Endress+Hauser',
            unitCost: 2800.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['filling_line', 'pasteurizer', 'mixer'],
            failureModes: ['electrode_coating', 'liner_damage', 'electronics_failure'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { size: '2 inch', type: 'Magnetic', accuracy: '0.2%', rating: 'EHEDG' },
          },
          {
            id: 'metal_detector_head',
            partNumber: 'MD-HEAD-300',
            name: 'Metal Detector Search Head',
            description: 'Replacement search head for 300mm aperture metal detector',
            category: 'sensors',
            manufacturer: 'Mettler Toledo',
            unitCost: 4500.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['packaging_machine', 'conveyor'],
            failureModes: ['coil_damage', 'sensitivity_loss', 'environmental_interference'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { aperture: '300mm', detection: 'Fe 1.0mm, Non-Fe 1.2mm, SS 1.5mm' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Food & Beverage)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'contamination',
        name: 'Contamination',
        description: 'Root causes related to product contamination and food safety',
        icon: 'AlertTriangle',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'microbiological_contamination',
            name: 'Microbiological Contamination',
            description: 'Bacterial, viral, or fungal contamination of product or equipment',
            category: 'contamination',
            symptoms: [
              'Positive pathogen test results',
              'Elevated ATP readings',
              'Product spoilage before expiration',
              'Off-odors in product',
              'Visible mold growth',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review ATP swab results from affected area',
                expectedResult: 'ATP readings above threshold indicate contamination',
                tools: ['ATP meter', 'Swabs'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Collect environmental samples for laboratory analysis',
                expectedResult: 'Identify specific organism type',
                tools: ['Sample swabs', 'Sterile containers'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Review sanitation records and CIP logs',
                expectedResult: 'Identify gaps in sanitation coverage or timing',
                tools: ['CMMS', 'CIP records'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Inspect equipment for dead legs, crevices, or damaged surfaces',
                expectedResult: 'Identify harborage points for bacteria',
                tools: ['Flashlight', 'Borescope'],
                duration: 45,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement environmental monitoring program',
              'Validate CIP cycles with temperature and chemical verification',
              'Eliminate dead legs in piping systems',
              'Maintain surface integrity of product contact surfaces',
              'Control facility humidity and temperature',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['product_contact_surfaces', 'piping', 'valves', 'tanks'],
            requiredExpertise: ['sme', 'technician'],
            estimatedResolutionTime: { min: 4, typical: 12, max: 48, unit: 'hours' },
            costRange: { min: 5000, max: 500000, currency: 'USD' },
            industryReferences: ['FDA FSMA', 'HACCP Principles', 'GMP Guidelines'],
          },
          {
            id: 'allergen_cross_contact',
            name: 'Allergen Cross-Contact',
            description: 'Unintended presence of allergens from shared equipment or production sequencing',
            category: 'contamination',
            symptoms: [
              'Positive allergen swab tests',
              'Customer allergic reaction reports',
              'Incorrect label declarations',
              'Product color or appearance variations',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review production schedule for allergen sequencing',
                expectedResult: 'Identify improper production order',
                tools: ['Production schedule', 'Allergen matrix'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Perform allergen-specific swab testing on equipment',
                expectedResult: 'Quantify allergen residue levels',
                tools: ['Allergen test kits', 'Swabs'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Review changeover cleaning procedures',
                expectedResult: 'Verify adequate cleaning between allergen products',
                tools: ['SOP documents', 'Cleaning records'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement allergen control program',
              'Validate changeover cleaning procedures',
              'Schedule allergen-containing products appropriately',
              'Use dedicated equipment where possible',
              'Train all personnel on allergen awareness',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['mixers', 'fillers', 'conveyors', 'packaging'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 2000, max: 100000, currency: 'USD' },
            industryReferences: ['FALCPA', 'FDA Allergen Guidelines', 'GFSI Allergen Requirements'],
          },
          {
            id: 'foreign_material',
            name: 'Foreign Material Contamination',
            description: 'Physical contamination from metal, plastic, glass, or other materials',
            category: 'contamination',
            symptoms: [
              'Metal detector rejects',
              'X-ray inspection failures',
              'Customer complaints of foreign material',
              'Visual contamination observed',
              'Equipment damage or wear patterns',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Collect and analyze foreign material sample',
                expectedResult: 'Identify material type and potential source',
                tools: ['Magnification', 'Material analysis'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Inspect upstream equipment for wear or damage',
                expectedResult: 'Identify source of contamination',
                tools: ['Flashlight', 'Inspection tools'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Review maintenance and repair records',
                expectedResult: 'Correlate with recent maintenance activities',
                tools: ['CMMS'],
                duration: 20,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Implement glass and brittle plastic policy',
              'Conduct regular equipment inspections',
              'Install and maintain detection equipment',
              'Use metal-detectable gaskets and components',
              'Establish pre-operational inspection checklist',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['screens', 'gaskets', 'blades', 'conveyor_belts', 'packaging'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 12, unit: 'hours' },
            costRange: { min: 500, max: 50000, currency: 'USD' },
            industryReferences: ['GFSI Foreign Material Guidelines', 'BRC Global Standard'],
          },
        ],
      },
      {
        id: 'mechanical',
        name: 'Mechanical',
        description: 'Root causes related to mechanical components and systems',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'pump_cavitation',
            name: 'Pump Cavitation',
            description: 'Vapor bubble formation and collapse in pump due to insufficient suction pressure',
            category: 'mechanical',
            symptoms: [
              'Crackling or rattling noise from pump',
              'Reduced flow rate',
              'Vibration increase',
              'Impeller erosion',
              'Inconsistent discharge pressure',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure suction pressure during operation',
                expectedResult: 'NPSH available should exceed NPSH required',
                tools: ['Pressure gauge'],
                duration: 10,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Check for obstructions in suction line',
                expectedResult: 'Clear flow path, no clogged strainers',
                tools: ['Inspection tools'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Verify product temperature',
                expectedResult: 'Temperature within pump design limits',
                tools: ['Temperature gauge'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Inspect impeller for erosion damage',
                expectedResult: 'No pitting or erosion on impeller surfaces',
                tools: ['Visual inspection'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Ensure adequate suction head',
              'Install properly sized strainers',
              'Maintain proper product temperature',
              'Avoid running pumps below minimum flow',
              'Regular impeller inspection',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['pumps', 'impellers', 'seals'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 1, typical: 3, max: 8, unit: 'hours' },
            costRange: { min: 300, max: 3000, currency: 'USD' },
            industryReferences: ['Hydraulic Institute Standards'],
          },
          {
            id: 'seal_failure',
            name: 'Mechanical Seal Failure',
            description: 'Failure of rotating equipment seals causing leakage or contamination',
            category: 'mechanical',
            symptoms: [
              'Product leakage around shaft',
              'Visible seal face damage',
              'Contaminated product',
              'Elevated seal temperature',
              'Unusual noise from seal area',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection for leakage',
                expectedResult: 'Identify leak location and severity',
                tools: ['Flashlight', 'Mirror'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check seal flush and cooling systems',
                expectedResult: 'Proper flow and temperature',
                tools: ['Temperature gauge', 'Flow indicator'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Measure shaft runout',
                expectedResult: 'Runout within specification',
                tools: ['Dial indicator'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Maintain proper seal flush',
              'Control operating temperature',
              'Ensure shaft alignment',
              'Use correct seal materials for product',
              'Replace seals at recommended intervals',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['mechanical_seals', 'o_rings', 'shaft_sleeves'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 2, typical: 4, max: 8, unit: 'hours' },
            costRange: { min: 400, max: 2500, currency: 'USD' },
          },
          {
            id: 'bearing_failure',
            name: 'Bearing Failure',
            description: 'Degradation of bearings due to wear, lubrication issues, or misalignment',
            category: 'mechanical',
            symptoms: [
              'Increased vibration',
              'Elevated temperature',
              'Audible noise',
              'Shaft wobble',
              'Metal particles in lubricant',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure vibration at bearing locations',
                expectedResult: 'Identify characteristic bearing frequencies',
                tools: ['Vibration analyzer'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Check bearing temperature',
                expectedResult: 'Temperature within normal range',
                tools: ['IR thermometer'],
                duration: 5,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Inspect lubricant condition',
                expectedResult: 'Clean lubricant, correct level',
                tools: ['Visual inspection'],
                duration: 10,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Implement vibration monitoring program',
              'Follow lubrication schedule',
              'Use correct lubricant type',
              'Ensure proper alignment',
              'Protect bearings from contamination',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['bearings', 'shafts', 'housings'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 2, typical: 4, max: 8, unit: 'hours' },
            costRange: { min: 200, max: 2000, currency: 'USD' },
            industryReferences: ['ISO 10816', 'SKF Bearing Analysis'],
          },
        ],
      },
      {
        id: 'sanitation',
        name: 'Sanitation',
        description: 'Root causes related to cleaning and sanitation processes',
        icon: 'Sparkles',
        color: '#10b981',
        rootCauses: [
          {
            id: 'cip_failure',
            name: 'CIP Cycle Failure',
            description: 'Inadequate cleaning during Clean-in-Place cycle due to parameters or equipment issues',
            category: 'sanitation',
            symptoms: [
              'Failed post-CIP ATP swabs',
              'Visible residue after cleaning',
              'Product quality issues',
              'Off-flavors in product',
              'Elevated microbiological counts',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review CIP chart recorder data',
                expectedResult: 'Verify time, temperature, and flow rate achieved',
                tools: ['CIP system records'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Verify chemical concentrations',
                expectedResult: 'Caustic and acid at target concentrations',
                tools: ['Titration kit', 'Conductivity meter'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Inspect spray devices and flow paths',
                expectedResult: 'Proper spray patterns, no blockages',
                tools: ['Visual inspection', 'Borescope'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Check valve sequencing and timing',
                expectedResult: 'All valves operating correctly',
                tools: ['PLC diagnostics'],
                duration: 20,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Regular CIP system validation',
              'Daily chemical concentration checks',
              'Spray device inspection schedule',
              'Flow rate verification',
              'Temperature monitoring alarms',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['spray_balls', 'valves', 'pumps', 'heat_exchangers'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 12, unit: 'hours' },
            costRange: { min: 500, max: 5000, currency: 'USD' },
            industryReferences: ['3-A Sanitary Standards', 'EHEDG Guidelines'],
          },
          {
            id: 'biofilm_formation',
            name: 'Biofilm Formation',
            description: 'Development of bacterial biofilm on equipment surfaces',
            category: 'sanitation',
            symptoms: [
              'Persistent positive swabs in same location',
              'Slimy residue on surfaces',
              'Recurring microbiological issues',
              'Chlorine demand increase',
              'Resistance to normal sanitation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Map positive swab locations over time',
                expectedResult: 'Identify persistent contamination sites',
                tools: ['Swab records', 'Facility map'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Inspect surfaces with UV light',
                expectedResult: 'Biofilm fluoresces under UV',
                tools: ['UV flashlight'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check surface condition for damage',
                expectedResult: 'Smooth, undamaged surfaces',
                tools: ['Visual inspection', 'Surface comparator'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement biofilm-specific treatment program',
              'Use enzymatic cleaners periodically',
              'Maintain surface finish quality',
              'Eliminate dead legs and low-flow areas',
              'Increase CIP frequency in problem areas',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['tanks', 'piping', 'valves', 'heat_exchangers'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 4, typical: 16, max: 48, unit: 'hours' },
            costRange: { min: 2000, max: 20000, currency: 'USD' },
            industryReferences: ['FDA Biofilm Guidance', 'EHEDG Biofilm Guidelines'],
          },
          {
            id: 'inadequate_manual_cleaning',
            name: 'Inadequate Manual Cleaning',
            description: 'Insufficient manual cleaning due to procedure, training, or time issues',
            category: 'sanitation',
            symptoms: [
              'Failed pre-operational inspection',
              'Visible soil after cleaning',
              'High ATP readings',
              'Inconsistent cleaning results',
              'Product contamination',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Observe cleaning procedure being performed',
                expectedResult: 'Identify deviations from SOP',
                tools: ['SOP document', 'Observation'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Verify chemical concentrations and contact time',
                expectedResult: 'Correct dilutions and dwell time',
                tools: ['Titration kit', 'Timer'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Review training records for sanitation staff',
                expectedResult: 'All personnel properly trained',
                tools: ['Training records'],
                duration: 10,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Standardize and document cleaning procedures',
              'Provide regular refresher training',
              'Allocate adequate time for cleaning',
              'Use visual management for cleaning status',
              'Implement verification swabbing program',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['all_equipment'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 30, typical: 90, max: 240, unit: 'minutes' },
            costRange: { min: 200, max: 2000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'process',
        name: 'Process',
        description: 'Root causes related to process control and operating conditions',
        icon: 'Activity',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'temperature_deviation',
            name: 'Temperature Deviation',
            description: 'Product temperature outside critical limits during processing',
            category: 'process',
            symptoms: [
              'Temperature alarm activation',
              'CCP deviation recorded',
              'Product quality issues',
              'Regulatory non-compliance',
              'Product hold or recall',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review temperature chart recorder data',
                expectedResult: 'Identify timing and magnitude of deviation',
                tools: ['Temperature records'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Verify sensor calibration',
                expectedResult: 'Sensors reading accurately',
                tools: ['Calibrated thermometer'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check heating/cooling system operation',
                expectedResult: 'System functioning correctly',
                tools: ['System diagnostics'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Review product flow rates',
                expectedResult: 'Flow within specification',
                tools: ['Flow meter'],
                duration: 15,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Calibrate temperature sensors regularly',
              'Maintain heating/cooling systems',
              'Implement redundant temperature monitoring',
              'Set early warning alarms',
              'Train operators on deviation response',
            ],
            typicalProbability: 0.14,
            affectedComponents: ['heat_exchangers', 'pasteurizers', 'coolers', 'sensors'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' },
            costRange: { min: 1000, max: 100000, currency: 'USD' },
            industryReferences: ['FDA PMO', 'HACCP CCP Guidelines'],
          },
          {
            id: 'fill_weight_variation',
            name: 'Fill Weight Variation',
            description: 'Inconsistent fill weights or volumes outside specification limits',
            category: 'process',
            symptoms: [
              'Checkweigher rejects',
              'Customer complaints of underfill',
              'Product giveaway (overfill)',
              'SPC charts out of control',
              'Regulatory weight violations',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Collect fill weight data sample',
                expectedResult: 'Determine Cp/Cpk and identify pattern',
                tools: ['Scale', 'SPC software'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Check filler valve operation',
                expectedResult: 'Consistent valve timing and seal integrity',
                tools: ['Visual inspection', 'Timing measurement'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Verify product supply pressure and temperature',
                expectedResult: 'Stable supply conditions',
                tools: ['Pressure gauge', 'Thermometer'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 4,
                action: 'Check container positioning',
                expectedResult: 'Consistent container placement',
                tools: ['Visual inspection'],
                duration: 10,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Implement SPC monitoring',
              'Regular filler valve maintenance',
              'Stabilize product supply conditions',
              'Calibrate checkweighers regularly',
              'Train operators on adjustment procedures',
            ],
            typicalProbability: 0.16,
            affectedComponents: ['filler_valves', 'flow_meters', 'load_cells', 'timing_systems'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 360, unit: 'minutes' },
            costRange: { min: 500, max: 10000, currency: 'USD' },
            industryReferences: ['FDA Net Contents', 'NIST Handbook 133'],
          },
          {
            id: 'changeover_error',
            name: 'Changeover Error',
            description: 'Incorrect setup after product or packaging changeover',
            category: 'process',
            symptoms: [
              'Wrong product in wrong package',
              'Incorrect label application',
              'Quality defects at line startup',
              'First article inspection failure',
              'Customer complaints',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review changeover checklist completion',
                expectedResult: 'All steps verified and signed',
                tools: ['Changeover records'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Verify first article inspection records',
                expectedResult: 'Proper inspection conducted',
                tools: ['QC records'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check line clearance procedures',
                expectedResult: 'Previous product completely removed',
                tools: ['Visual inspection'],
                duration: 15,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Use standardized changeover checklists',
              'Implement two-person verification',
              'Require first article approval before production',
              'Use barcode scanning for material verification',
              'Train operators on changeover procedures',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['all_equipment'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 30, typical: 60, max: 180, unit: 'minutes' },
            costRange: { min: 500, max: 50000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'sanitation_cascade',
        name: 'Sanitation Failure Cascade',
        description: 'Progression from sanitation deficiency to product contamination',
        sequence: [
          'CIP parameter drift or failure',
          'Residual soil accumulation',
          'Biofilm formation initiation',
          'Microbiological growth',
          'Product contamination',
          'Quality hold or recall',
        ],
        indicators: [
          'Trending ATP readings',
          'Inconsistent CIP parameters',
          'Environmental swab positives',
        ],
        preventionStrategy: 'Implement CIP monitoring with automatic alerts and trending analysis',
      },
      {
        id: 'temperature_abuse',
        name: 'Cold Chain Temperature Abuse',
        description: 'Progressive degradation due to temperature control failure',
        sequence: [
          'Refrigeration efficiency decline',
          'Temperature fluctuations begin',
          'Product temperature rises',
          'Accelerated microbial growth',
          'Product quality degradation',
          'Food safety incident',
        ],
        indicators: [
          'Compressor runtime increase',
          'Temperature alarms',
          'Refrigerant pressure changes',
        ],
        preventionStrategy: 'Continuous temperature monitoring with predictive maintenance on refrigeration equipment',
      },
      {
        id: 'seal_contamination',
        name: 'Seal Degradation to Contamination',
        description: 'Progression from seal wear to product contamination',
        sequence: [
          'Seal material degradation',
          'Micro-leakage begins',
          'Seal harbors bacteria',
          'Product contact contamination',
          'Microbiological failures',
        ],
        indicators: [
          'Visible seal wear',
          'Minor drips or leaks',
          'Localized swab failures',
        ],
        preventionStrategy: 'Preventive seal replacement program based on runtime hours and product exposure',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES (Food & Beverage Industry)
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'maintenance_technician', name: 'Maintenance Technician', hourlyRate: 48, currency: 'USD', certifications: ['Food Safety', 'Lockout/Tagout'] },
      { roleId: 'sanitation_technician', name: 'Sanitation Technician', hourlyRate: 35, currency: 'USD', certifications: ['Food Safety', 'Chemical Handling'] },
      { roleId: 'quality_technician', name: 'Quality Technician', hourlyRate: 42, currency: 'USD', certifications: ['HACCP', 'SQF Practitioner'] },
      { roleId: 'process_operator', name: 'Process Operator', hourlyRate: 38, currency: 'USD', certifications: ['Food Safety', 'CIP Operation'] },
      { roleId: 'food_safety_manager', name: 'Food Safety Manager', hourlyRate: 75, currency: 'USD', certifications: ['PCQI', 'SQF Practitioner', 'HACCP'] },
      { roleId: 'quality_manager', name: 'Quality Assurance Manager', hourlyRate: 72, currency: 'USD', certifications: ['ASQ CQE', 'SQF Practitioner'] },
      { roleId: 'production_manager', name: 'Production Manager', hourlyRate: 68, currency: 'USD', certifications: ['Food Safety', 'Lean Manufacturing'] },
      { roleId: 'contractor_technician', name: 'Contract Technician', hourlyRate: 65, currency: 'USD' },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
